import { strToU8 } from "fflate";
import { BLOCK_DEFINITIONS } from "../core/blockRegistry.ts";
import type { AiWorkspace } from "./workspace.ts";

function escapeXml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

// Orthographic block maps provide coordinate evidence without a browser or assets.
export function createProjection(workspace: AiWorkspace, view: "top" | "front" | "east"): string {
  const entries = workspace.world.entries().sort((a,b)=>a.y-b.y || a.z-b.z || a.x-b.x);
  const horizontal = view === "east" ? "z" : "x";
  const vertical = view === "top" ? "z" : "y";
  const depth = view === "top" ? "y" : view === "east" ? "x" : "z";
  const bounds = entries.length ? workspace.world.getBounds() : { min: {x:0,y:0,z:0}, max:{x:0,y:0,z:0} };
  const width = bounds.max[horizontal] - bounds.min[horizontal] + 1;
  const height = bounds.max[vertical] - bounds.min[vertical] + 1;
  const stride = Math.max(1, Math.ceil(Math.max(width, height) / 96));
  const columns = Math.ceil(width / stride), rows = Math.ceil(height / stride), size = 9, margin = 60;
  const pixels = new Map<string, typeof entries[number]>();
  for (const block of entries) {
    const column = Math.floor((block[horizontal] - bounds.min[horizontal]) / stride);
    const row = Math.floor((block[vertical] - bounds.min[vertical]) / stride);
    const key = column + "," + row;
    const previous = pixels.get(key);
    if (!previous || block[depth] > previous[depth] || (block[depth] === previous[depth] && block.blockSpec < previous.blockSpec)) pixels.set(key, block);
  }
  const colors = new Map(BLOCK_DEFINITIONS.map(item => [item.id, item.color]));
  const used = [...new Set([...pixels.values()].map(item => item.blockId))].sort();
  const svgWidth = Math.max(650, columns * size + 2 * margin);
  const plotHeight = rows * size + margin + 30;
  const legendRows = Math.ceil(used.length / 3);
  const svgHeight = plotHeight + 70 + legendRows * 20;
  const parts = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="' + svgWidth + '" height="' + svgHeight + '" viewBox="0 0 ' + svgWidth + " " + svgHeight + '">',
    '<rect width="100%" height="100%" fill="#101b2c"/>',
    '<g font-family="monospace" font-size="12" fill="#e7edf4">',
    '<text x="16" y="22">' + escapeXml(workspace.name + " · " + view + " · " + workspace.revision) + "</text>",
    '<text x="16" y="42">' + horizontal.toUpperCase() + " horizontal / " + vertical.toUpperCase() + " vertical · local coordinates · " + stride + " block(s) per cell</text>"
  ];
  for (const [key, block] of pixels) {
    const [column, row] = key.split(",").map(Number);
    const screenRow = view === "top" ? row : rows - 1 - row;
    parts.push('<rect x="' + (margin + column * size) + '" y="' + (margin + screenRow * size) + '" width="' + size + '" height="' + size + '" fill="' + escapeXml(colors.get(block.blockId) ?? "#87909a") + '"><title>' +
      escapeXml([block.x, block.y, block.z].join(",") + " " + block.blockSpec) + "</title></rect>");
  }
  const labelEvery = Math.max(1, Math.ceil(Math.max(columns, rows) / 8));
  for (let column = 0; column < columns; column += labelEvery) parts.push('<text x="' + (margin + column * size) + '" y="' + (margin + rows * size + 20) + '">' + (bounds.min[horizontal] + column * stride) + "</text>");
  for (let row = 0; row < rows; row += labelEvery) {
    const logical = view === "top" ? row : rows - 1 - row;
    parts.push('<text x="4" y="' + (margin + row * size + 8) + '">' + (bounds.min[vertical] + logical * stride) + "</text>");
  }
  parts.push('<text x="16" y="' + (plotHeight + 20) + '">Nearest visible block per cell; air is background. Geometry/textures are approximated.</text>');
  for (let index = 0; index < used.length; index++) {
    const x = 16 + (index % 3) * 210, y = plotHeight + 42 + Math.floor(index / 3) * 20;
    parts.push('<rect x="' + x + '" y="' + (y - 9) + '" width="10" height="10" fill="' + escapeXml(colors.get(used[index]) ?? "#87909a") + '"/>');
    parts.push('<text x="' + (x + 15) + '" y="' + y + '">' + escapeXml(used[index]) + "</text>");
  }
  parts.push("</g></svg>");
  return parts.join("\n");
}

export function createReviewFiles(workspace: AiWorkspace): Record<string, Uint8Array> {
  const project = workspace.getProject();
  const files: Record<string, Uint8Array> = {
    "project.json": strToU8(JSON.stringify(project, null, 2)),
    "analysis.json": strToU8(JSON.stringify(workspace.analyze(), null, 2)),
    "review-template.json": strToU8(JSON.stringify({
      format:"minecraft-voxel-review",version:1,sceneRevision:project.sceneRevision,findings:[]
    }, null, 2)),
    "README.txt": strToU8(
      "Read project.json for exact blocks, state, block entities, intent, named regions and history.\n" +
      "Read analysis.json for geometric/material candidates; these are observations, not aesthetic judgments.\n" +
      "views/*.svg are coordinate maps with legends. For stride > 1 each cell aggregates multiple blocks and keeps the nearest visible block.\n" +
      "Browser bundles also contain renders/*.png and renders/cameras.json; inspect rendering mode and layer clipping before interpreting them.\n" +
      "Review against sceneRevision " + project.sceneRevision + ". Fill review-template.json with findings: id, author, severity (info/warning/error), message, inclusive bounds {min:{x,y,z},max:{x,y,z}}, optional suggestion.\n" +
      "Describe why a placement conflicts with the design brief. Cite local integer coordinates (X east, Y up, Z south).\n" +
      "Return the review JSON; the editor and CLI can import, focus, resolve and preserve findings.\n"
    )
  };
  for (const view of ["top", "front", "east"] as const) files["views/" + view + ".svg"] = strToU8(createProjection(workspace, view));
  return files;
}
